/**
 * Pure view model of «Распределение по ЦА» (owner wave 5, task T.W1) — the same
 * framework-free rule the other page models follow: the `unit` Playwright project imports
 * this in plain Node, so nothing here may touch React or `import.meta.env`.
 *
 * THE COLUMN MODEL (T.Z35 — the owner rejected the (course × format) grid): ONE column
 * per course id, exactly the owner's canonical table. The api still answers the
 * dictionary crossed with both formats (24 `(courseId, format)` pairs, the frozen #11.5
 * shape), and the web collapses them here — because each campaign course RUNS one format,
 * so the second column of every pair is a phantom that only ever shows the applications
 * the source tagged with the other format. The owner read that split as «offline &
 * online counted incorrectly»: a course's applications must sit in the course's ONE
 * column, whatever `format` the source carried, and a column count is therefore the SUM
 * of the api's cells for that id.
 *
 * Three more decisions this file owns:
 *   * the caption — the dictionary's short name (T.Z25/T.Z15) when the ADMIN set one,
 *     the bare courseId ONLY as the fallback. No id suffix beside it (the owner deleted
 *     that rendering);
 *   * the MERGED caption cells (T.Z35c) — consecutive columns that print the same caption
 *     are ONE header cell ({@link courseGroups}), so «ИИ 8-9» is written once over its
 *     офл/онл pair, the way the owner's own sheet does it;
 *   * the traffic light a cell is lit with against the venue's plan for the format the
 *     course runs (#21's `formats`), falling back to the venue's whole plan while the
 *     dictionary is silent.
 */

import type { AudienceWarning, CaCourseColumn, CaRow, CaTotals, CellFormat, CellKey } from '../../api/ca-distribution';
// ADDITIVE (v1.9.33, T.Z141) — a VALUE import, the pivot's counted statuses: the same
// array the api's grid groups by travels in every cell's drill-down link (the unit
// project resolves the built `dist`, like the audience fold in `applications-model`).
import { CA_COUNTED_STATUSES } from '@cb-manager/shared';
import { formatTag, type CoursePillSource } from '../quotas/quotas-model';
import { parseDepartmentBucket, rowsInDepartmentBucket, type DepartmentBucket } from '../../departments';

// ---------------------------------------------------------------------------
// The URL
// ---------------------------------------------------------------------------

/** The toggle lives in the URL, like every filter on every page of this app. */
export const PARAM_EXCLUDE_COLLEGES = 'excludeColleges';

/**
 * The venue filter (T.V7) — the SAME key the register (`#3`) and the quota grid (`#12`)
 * already read, so a deep link built for one page keeps its meaning on another. The
 * `/ca-distribution` endpoint takes `excludeColleges` ONLY (any other key is 400
 * VALIDATION), so this filter is applied client-side on the page — see
 * {@link filterRowsByVenue}.
 */
export const PARAM_VENUE_ID = 'venueId';

/**
 * ADDITIVE (T.Z56) — the «Отдел» filter (`OP|OS|NONE|MIXED`), the SAME key the register
 * and the teachers roster read. CLIENT-SIDE here on purpose: `/ca-distribution` answers
 * the whole venue-joined roster in ONE unpaginated read (the same reason the venue
 * combobox is client-side), the api takes `excludeColleges`/`formats` only, and the
 * totals row already recomputes from the surviving rows — see {@link filterRowsByVenue}.
 * A venue's bucket is the departments of its managers (`src/departments.ts`, the T.Z42
 * fold); the api-side surfaces (#3/#7.5/#11) narrow the same buckets server-side.
 */
export const PARAM_DEPARTMENT = 'department';

/** The URL fold: a hand-edited value is «Все», never a request the api would 400. */
export function parseDepartmentParam(value: string | undefined): DepartmentBucket | undefined {
  return parseDepartmentBucket(value);
}

/**
 * `true` when absent: «исключить колледжи» is the page's DEFAULT (the meeting's problem is
 * in the schools), so a bare `/ca-distribution` link opens the schools view.
 */
export function parseExcludeParam(value: string | undefined): boolean {
  if (value === undefined) return true;
  const folded = value.trim().toLowerCase();
  return !(folded === 'false' || folded === '0' || folded === 'no');
}

/** The canonical URL spelling of the toggle (`true`/`false`, the api's own vocabulary). */
export function excludeParamValue(exclude: boolean): string {
  return exclude ? 'true' : 'false';
}

/**
 * Absent = no venue filter. Digits only (`^\d+$`, the register's own `parseIntParam`
 * rule): a venue id is always written `String(number)` by this app, so `12.5`, `0`,
 * `-3` and anything else is a hand-edited link — it filters to nothing rather than to
 * whatever `parseInt` would salvage from it.
 */
export function parseVenueIdParam(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

// ---------------------------------------------------------------------------
// The venue filter (T.V7) — client-side, the endpoint answers a whole pivot
// ---------------------------------------------------------------------------

/**
 * The rows the venue filter leaves. `undefined` is «Все площадки» — nothing dropped, and
 * the api's own row order is kept (a copy, so a caller can never mutate the query cache).
 */
export function filterRowsByVenue(rows: readonly CaRow[], venueId: number | undefined): CaRow[] {
  return venueId === undefined ? [...rows] : rows.filter((row) => row.venueId === venueId);
}

/**
 * ADDITIVE (T.Z56) — the rows the «Отдел» filter leaves, composed AFTER the venue cut
 * (the two AND naturally: a venue is in one bucket or not in the department at all). The
 * buckets come pre-computed off the #6 × #25.5 join; `undefined` is «Все».
 */
export function filterRowsByDepartment(
  rows: readonly CaRow[],
  buckets: ReadonlyMap<number, DepartmentBucket>,
  department: DepartmentBucket | undefined,
): CaRow[] {
  return rowsInDepartmentBucket(rows, buckets, department);
}

/**
 * The totals row of the view IN SCOPE.
 *
 * The api answers the totals of the whole pivot it returned (`buildCaDistribution` sums
 * the rows it emitted), so a client-side venue filter has to recompute them from the rows
 * it kept — same arithmetic, over the same per-cell keys and per-format plans — or the
 * totals row would disagree with the one row sitting above it. The totals stay keyed by
 * the api's own `"courseId:FORMAT"` cell keys (the #11.5 shape), so an unfiltered view
 * re-uses the api's numbers untouched and a column reads its sum through
 * {@link columnCount}.
 */
export function totalsOfRows(rows: readonly CaRow[], courses: readonly CaCourseColumn[]): CaTotals {
  const totals: CaTotals = { cells: {}, plans: { OFFLINE: 0, ONLINE: 0 }, total: 0 };
  for (const course of courses) totals.cells[cellKey(course.courseId, course.format)] = 0;

  for (const row of rows) {
    for (const course of courses) {
      const key = cellKey(course.courseId, course.format);
      const count = row.cells[key] ?? 0;
      totals.cells[key] = (totals.cells[key] ?? 0) + count;
      totals.total += count;
    }
    totals.plans.OFFLINE += cellPlan(row, 'OFFLINE');
    totals.plans.ONLINE += cellPlan(row, 'ONLINE');
  }

  return totals;
}

// ---------------------------------------------------------------------------
// The columns — ONE per course id (T.Z35)
// ---------------------------------------------------------------------------

/**
 * One column of the grid: one campaign course, the owner's canonical table. `keys` lists
 * every api cell the column sums (the OFFLINE and the ONLINE half of the id — usually one
 * of them is the course's own format and the other stays 0), so a count never depends on
 * which format the source tagged the applications with.
 */
export interface CourseColumn {
  courseId: number;
  /** The api cell keys this column sums, OFFLINE first — `["31599:OFFLINE","31599:ONLINE"]`. */
  keys: CellKey[];
  /**
   * The format the course runs, from #21's `formats` — the row-2 tag, the column tint and
   * the traffic light's plan denominator all read it. `undefined` while #21 is silent (or
   * names both formats): no tag, no tint, and the plan is the venue's whole plan.
   */
  format: CellFormat | undefined;
  /** The row-2 sub-header: «офл»/«онл», or `''` while the format is unknown. */
  label: string;
  /** The full ДОП title: the header's tooltip, so a short caption never hides a course. */
  title: string;
  /** The dictionary's short caption (T.Z25), normalized — `null` ⇒ the id is the caption. */
  shortName: string | null;
  /**
   * T.Z37 — the sweep's grade-vs-audience verdict for this course, as the api answered it
   * (`null` = not flagged; both format entries of the course carry the same verdict, the
   * first non-null one is read).
   */
  audienceWarning: AudienceWarning | null;
}

/**
 * The dictionary's short caption (T.Z25), normalized: whitespace trimmed, and an empty
 * string or an absent value collapses to `null` — the one value that means «show the id».
 */
function shortNameOf(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The caption of one column (T.Z25/T.Z35): the short name where the dictionary has one
 * («ИИ 8-9»), else the bare courseId. NO id suffix beside the name any more — the owner
 * rejected the «ИИ 8-9 31599» gluing (T.Z25's rendering); the id lives in the tooltip
 * ({@link courseTitle}), where it is copied together with the ДОП title.
 */
export function courseCaption(courseId: number, shortName: string | null | undefined): string {
  const value = shortName?.trim() ?? '';
  return value === '' ? String(courseId) : value;
}

/**
 * The caption's tooltip (T.V3): the id AND the full ДОП title — «31599 — Разработчик
 * интеллектуальных решений. Базовый уровень для 8-9 кл.». The caption is short by design,
 * so the tooltip is the one place the column resolves to a course; leading with the id
 * means the reader copies the two together, the way a quota edit asks for them. A course
 * #21 does not know (or names with an empty string) stays «Курс N» — there is no name to
 * pair the id with.
 */
export function courseTitle(courseId: number, programName: string | undefined): string {
  if (programName === undefined) return `Курс ${courseId}`;
  const name = programName.trim();
  return name === '' ? `Курс ${courseId}` : `${courseId} — ${name}`;
}

/** The OFFLINE-first canonical order — the order every grid in this app renders. */
const CELL_FORMATS: ReadonlyArray<CellFormat> = ['OFFLINE', 'ONLINE'];

/**
 * The columns of the grid, from the api's own `courses` (#11.5) and #21's dictionary —
 * the ids are never hardcoded here.
 *
 * THE ORDER is the api's ids ascending, which IS the owner's canonical sheet: the id
 * allocation encodes programme + audience + format, so ascending gives the ИИ block
 * (31599…31613) before the Python block (31614…31619) and, inside a block, the audience
 * pairs (8-9, 10-11, СПО) with the offline course before its online twin — under the
 * owner-confirmed short-name map (tracker T.Z15/T.Z35) that is exactly
 * «ИИ 8-9 офл, ИИ 8-9 онл, ИИ 10-11 офл, …». One rule, no hardcoded course list.
 *
 * THE COLLAPSE (T.Z35): the api's `(courseId, format)` pairs are grouped by id — the
 * duplication the owner rejected — and every pair's key feeds the one column, so the
 * count of a column is the count of the course's applications, not of one format slice.
 *
 * Two guards kept from the earlier grid (T.W1's «Курс 0 / 0» screenshot):
 *   * the seed's VENUE-LEVEL sentinel (`courseId` 0) is not a programme — it is the
 *     bucket the venue's plan travels on (T.W2), and as a column it can only be noise.
 *     The api filters it already; the grid does not trust that;
 *   * a duplicated api column can never add a second key to the same id.
 */
export function courseColumns(
  courses: readonly CaCourseColumn[],
  programs: readonly CoursePillSource[],
): CourseColumn[] {
  const byCourse = new Map(programs.map((program) => [program.courseId, program] as const));
  const formatsById = new Map<number, Set<CellFormat>>();
  const shortNameById = new Map<number, string | null>();
  const warningById = new Map<number, AudienceWarning>();

  for (const course of courses) {
    if (course.courseId <= 0) continue;
    const formats = formatsById.get(course.courseId) ?? new Set<CellFormat>();
    formats.add(course.format);
    formatsById.set(course.courseId, formats);
    if (!shortNameById.has(course.courseId)) shortNameById.set(course.courseId, shortNameOf(course.shortName));
    // T.Z37 — both format entries of a course carry the same verdict; the first non-null
    // one is the course's.
    if (course.audienceWarning !== undefined && course.audienceWarning !== null) {
      warningById.set(course.courseId, course.audienceWarning);
    }
  }

  return [...formatsById.keys()]
    .sort((a, b) => a - b)
    .map((courseId) => {
      const program = byCourse.get(courseId);
      // The format the course runs: #21's word when it names exactly one (every campaign
      // course does), `undefined` while the dictionary has not answered or is ambiguous.
      const own = (program?.formats ?? []).filter((format): format is CellFormat =>
        CELL_FORMATS.includes(format as CellFormat),
      );
      const format = own.length === 1 ? own[0] : undefined;
      // The api's keys for the id, OFFLINE first — the sum does not care, the tests do.
      const emitted = formatsById.get(courseId) ?? new Set<CellFormat>();
      const keys = CELL_FORMATS.filter((format) => emitted.has(format)).map((format) => cellKey(courseId, format));
      // T.Z25 — the dictionary's caption (#21) wins, the column's own field (#11.5) fills
      // in only while #21 has not answered for the course at all; a #21 `null` (the ADMIN
      // cleared the name) is an ANSWER, so the id stays the caption.
      const shortName =
        program === undefined ? (shortNameById.get(courseId) ?? null) : shortNameOf(program.shortName);
      return {
        courseId,
        keys,
        format,
        label: format === undefined ? '' : formatTag(format),
        title: courseTitle(courseId, program?.name),
        shortName,
        audienceWarning: warningById.get(courseId) ?? null,
      };
    });
}

// ---------------------------------------------------------------------------
// Reading the cells
// ---------------------------------------------------------------------------

/** `"courseId:FORMAT"` — the api's own cell key (same spelling both sides). */
export function cellKey(courseId: number, format: CellFormat): CellKey {
  return `${courseId}:${format}`;
}

/**
 * The count of one column: the SUM of the course's api cells over both formats (T.Z35 —
 * a course's applications sit in its one column whatever format the source tagged them
 * with). Reads a venue row and the totals row alike — the two share the shape.
 */
export function columnCount(source: CaRow | CaTotals, column: CourseColumn): number {
  let sum = 0;
  for (const key of column.keys) sum += source.cells[key] ?? 0;
  return sum;
}

/** The plan a source (row or totals) holds for one FORMAT — the traffic light's denominator. */
export function cellPlan(source: CaRow | CaTotals, format: CellFormat): number {
  return source.plans[format] ?? 0;
}

/**
 * The plan a column is measured against: the venue's plan for the format the course runs
 * (#21). While the format is unknown the denominator is the venue's WHOLE plan — a
 * denominator of only one format could understate the plan a course is filling, and the
 * light must never go red on a guess.
 */
export function columnPlan(source: CaRow | CaTotals, column: CourseColumn): number {
  if (column.format === undefined) return cellPlan(source, 'OFFLINE') + cellPlan(source, 'ONLINE');
  return cellPlan(source, column.format);
}

// ---------------------------------------------------------------------------
// The merged caption cells (T.Z35c) — ONE header cell per caption run
// ---------------------------------------------------------------------------

/**
 * One merged cell of the caption row: the run of columns that print the same caption
 * («ИИ 8-9» is written ONCE over its офл/онл pair), the columns under it and where the
 * run starts. The `colSpan` the header cell takes is `columns.length`.
 */
export interface CourseGroup {
  /** The caption the merged cell prints — the run's shared short name. */
  caption: string;
  /** The tooltip: every column of the run as «id — ДОП title», one per line. */
  title: string;
  /** The columns the cell spans, in render order. */
  columns: CourseColumn[];
  /** The render index of the run's first column (the `data-col` the run starts at). */
  from: number;
  /**
   * T.Z37 — the first warning found under the caption (`null` when none of the run's
   * courses is flagged). A run is normally ONE course, so this is the course's verdict;
   * a run that spans several keeps the loudest one and every verdict is named in the
   * tooltip ({@link courseGroupTitle}).
   */
  audienceWarning: AudienceWarning | null;
}

/**
 * The caption row of the header (T.Z35c): CONSECUTIVE columns that print the same
 * caption merge into one cell. The owner's canonical order puts a course's офл and онл
 * twins side by side, so the six short names give six cells of two columns — but the
 * rule is positional, not "pairs": a dictionary that names three neighbours alike
 * merges three, two that differ split.
 *
 * Merging by caption rather than by course block is what keeps the header honest when
 * #21 has named only part of the campaign: an unnamed course falls back to its bare id
 * ({@link courseCaption}), which merges with nothing — no id is ever read as another
 * course's caption.
 */
export function courseGroups(columns: readonly CourseColumn[]): CourseGroup[] {
  const runs: Array<{ caption: string; columns: CourseColumn[]; from: number }> = [];
  for (let index = 0; index < columns.length; index += 1) {
    const column = columns[index]!;
    const caption = courseCaption(column.courseId, column.shortName);
    const last = runs[runs.length - 1];
    if (last !== undefined && last.caption === caption) {
      last.columns.push(column);
      continue;
    }
    runs.push({ caption, columns: [column], from: index });
  }
  return runs.map((run) => ({
    ...run,
    title: courseGroupTitle(run.columns),
    audienceWarning: run.columns.find((column) => column.audienceWarning !== null)?.audienceWarning ?? null,
  }));
}

/**
 * The merged cell's tooltip — T.V3 survives the merge: every course under the caption as
 * «id — ДОП title», one per line. A cell that spans two courses must resolve BOTH, or the
 * caption would hide a course a quota edit asks for by id. T.Z37 — a flagged course adds
 * its ⚠ line under its own title, so the numbers travel with the course they belong to.
 */
export function courseGroupTitle(columns: readonly CourseColumn[]): string {
  return columns
    .map((column) =>
      column.audienceWarning === null ? column.title : `${column.title}\n${audienceWarningHint(column.audienceWarning)}`,
    )
    .join('\n');
}

/**
 * The ⚠ tooltip (T.Z37), the numbers included: «64% заявлений с классом вне аудитории
 * курса (7 из 11; ожидается: классы 10-11) — проверьте маппинг». The outside COUNT is
 * derived from the share (`round(share · seen)` — the share is stored full-precision, so
 * the product is exact), because the contract ships the share and not the count.
 */
export function audienceWarningHint(warning: AudienceWarning): string {
  const percent = Math.round(warning.share * 100);
  const outside = Math.round(warning.share * warning.seen);
  return (
    `${percent}% заявлений с классом вне аудитории курса ` +
    `(${formatRu(outside)} из ${formatRu(warning.seen)}; ожидается: ${warning.expected}) — проверьте маппинг`
  );
}

/** Thousands-separated, the way every other count on this page reads (`formatCount`'s job in JSX). */
function formatRu(value: number): string {
  return value.toLocaleString('ru-RU');
}

// ---------------------------------------------------------------------------
// The drill-down
// ---------------------------------------------------------------------------

/**
 * The `/applications` URL one cell opens. A column aggregates the course's formats
 * (T.Z35 — its count is the course's applications), so the link carries the venue and
 * the course and NO format: #3 reads an absent `format` as «don't care», which makes the
 * list behind the click exactly the count the cell shows. Carrying either single format
 * would open a SHORTER list and the numbers would disagree.
 *
 * ADDITIVE (v1.9.33, T.Z141) — the link also carries the statuses the cell COUNTS
 * (`?status=LINK_RECEIVED,EVI_PASSED`, the api's comma-list grammar): without it a «1»
 * opened every application of the course at the venue, whatever its status, and the
 * register disagreed with the number the manager clicked. The list is the SHARED
 * `CA_COUNTED_STATUSES` — the very constant the api's pivot groups by — so the grid and
 * the link cannot drift.
 */
export function cellHref(venueId: number, courseId: number): string {
  const search = new URLSearchParams();
  search.set('venueId', String(venueId));
  search.set('courseId', String(courseId));
  search.set('status', CA_COUNTED_STATUSES.join(','));
  // `URLSearchParams` would percent-encode the list's comma into `%2C`; a comma is legal
  // raw inside a query component, the api decodes both spellings to the same value, and
  // the readable one is what the contract and the register's address bar should show.
  return `/applications?${search.toString().replace(/%2C/gi, ',')}`;
}

// The venue card a row's NAME opens (T.Z35c) is `venueCardHref` from the quotas model —
// the SAME builder the quota grid's own venue-name link uses, so the two grids can never
// disagree about the `/venues?id=` shape (the card is a query param, see VenuesPage).

// ---------------------------------------------------------------------------
// The traffic light
// ---------------------------------------------------------------------------

export type TrafficLight = 'green' | 'orange' | 'red' | 'none';

/** The plan a cell is measured against, in the owner's words: green ≤80%, orange 80-99%, red ≥100%. */
export function trafficLight(count: number, plan: number): TrafficLight {
  if (plan <= 0) {
    // No plan to fill: an application with nowhere to sit is the loudest signal there is.
    return count > 0 ? 'red' : 'none';
  }
  const percent = (count / plan) * 100;
  if (percent <= 80) return 'green';
  if (percent < 100) return 'orange';
  return 'red';
}

/** The cell's tooltip: what the colour means, in numbers. */
export function trafficHint(count: number, plan: number): string {
  if (plan <= 0) return count > 0 ? 'Плана нет, заявления есть' : 'Плана и заявлений нет';
  const percent = Math.round((count / plan) * 100);
  return `${count} из ${plan} · ${percent}%`;
}

/** The same arithmetic for the totals row — the campaign's own light per column. */
export function totalsCellLight(totals: CaTotals, column: CourseColumn): TrafficLight {
  return trafficLight(columnCount(totals, column), columnPlan(totals, column));
}
